/**
 * Seção "Nossos Planos" da landing page.
 *
 * Carrega os 16 planos da API e permite ao visitante alternar entre:
 *   - Modalidade: Individual (1 vida) | Coletivo (3 vidas)
 *   - Ciclo: Mensal | Anual (10% off)
 *
 * Os 4 cards (Bronze/Prata/Ouro/Diamante) são renderizados a partir do nível.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { CicloPagamento, ModalidadePlano, formatBRL } from '../../api/planos';
import { usePlanos } from '../../hooks/usePlanos';

const NIVEIS_ORDEM = ['bronze', 'prata', 'ouro', 'diamante'] as const;

const DESTAQUE_DESCRICAO: Record<string, string> = {
  bronze: 'Consultas médicas ilimitadas',
  prata: 'Consulta garantida com especialista',
  ouro: 'Maiores cuidados, com check-up gratuito',
  diamante: 'Melhor e mais avançado controle da saúde',
};

const CORES_BORDA: Record<string, string> = {
  bronze: 'border-orange-300/40',
  prata: 'border-slate-300/50',
  ouro: 'border-amber-400/50 ring-4 ring-amber-400/10 shadow-2xl scale-105 z-10',
  diamante: 'border-cyan-300/50',
};

export default function PlansSection() {
  const [modalidade, setModalidade] = useState<ModalidadePlano>('individual');
  const [ciclo, setCiclo] = useState<CicloPagamento>('mensal');
  const { loading, error, bronze, prata, ouro, diamante } = usePlanos(modalidade, ciclo);

  const planosPorNivel = { bronze, prata, ouro, diamante };

  function beneficiosDoPlano(nivel: string, dados: any): string[] {
    if (!dados) return [];
    const lista: string[] = [
      'Consultas ilimitadas com clínico geral, sem agendamento',
      'Atendimento 24h por dia, 7 dias por semana',
    ];
    if (dados.consultas_especialista_ano > 0) {
      lista.push(
        `${dados.consultas_especialista_ano} consulta${
          dados.consultas_especialista_ano > 1 ? 's' : ''
        } com médico especialista por ano`,
      );
    }
    if (dados.check_up_anual > 0) {
      lista.push(
        `${dados.check_up_anual} check-up anual${
          dados.check_up_anual > 1 ? ' (por vida coberta)' : ' (mulher, homem ou criança)'
        }`,
      );
    }
    lista.push('Receitas e atestados digitais com certificação CFM (ICP-Brasil)');
    lista.push('Descontos em medicamentos e exames');
    lista.push('Programa "Medicamento em Casa" (DF e entorno)');
    if (dados.vidas_incluidas > 1) {
      lista.unshift(`Cobertura para até ${dados.vidas_incluidas} vidas`);
    }
    return lista;
  }

  return (
    <section id="planos" className="section-padding bg-bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-black text-text-main mb-6 font-display">
            Nossos Planos
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto text-lg font-medium">
            Cuidado preventivo e resolutivo com o melhor custo-benefício do mercado.
          </p>
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <div className="inline-flex p-1 bg-white rounded-2xl border border-text-muted/10 shadow-sm">
            <button
              onClick={() => setModalidade('individual')}
              className={`px-6 py-2 rounded-xl text-sm font-bold transition-all ${
                modalidade === 'individual'
                  ? 'bg-primary text-white shadow'
                  : 'text-text-main/70 hover:text-text-main'
              }`}
            >
              Individual
            </button>
            <button
              onClick={() => setModalidade('coletivo')}
              className={`px-6 py-2 rounded-xl text-sm font-bold transition-all ${
                modalidade === 'coletivo'
                  ? 'bg-primary text-white shadow'
                  : 'text-text-main/70 hover:text-text-main'
              }`}
            >
              Coletivo (3 vidas)
            </button>
          </div>
          <div className="inline-flex p-1 bg-white rounded-2xl border border-text-muted/10 shadow-sm">
            <button
              onClick={() => setCiclo('mensal')}
              className={`px-6 py-2 rounded-xl text-sm font-bold transition-all ${
                ciclo === 'mensal'
                  ? 'bg-primary text-white shadow'
                  : 'text-text-main/70 hover:text-text-main'
              }`}
            >
              Mensal
            </button>
            <button
              onClick={() => setCiclo('anual')}
              className={`px-6 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2 ${
                ciclo === 'anual'
                  ? 'bg-primary text-white shadow'
                  : 'text-text-main/70 hover:text-text-main'
              }`}
            >
              Anual
              <span className="text-[10px] bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full font-black">
                -10%
              </span>
            </button>
          </div>
        </div>

        {error && (
          <div className="text-center text-red-600 bg-red-50 border border-red-200 rounded-xl p-4 mb-8 max-w-xl mx-auto">
            Não foi possível carregar os planos: {error}
          </div>
        )}

        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[500px] rounded-[2.5rem] bg-white/80 border border-text-muted/10 animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {NIVEIS_ORDEM.map((nivel) => {
              const plano = planosPorNivel[nivel];
              if (!plano) return null;
              const beneficios = beneficiosDoPlano(nivel, plano);
              return (
                <div
                  key={nivel}
                  className={`flex flex-col p-10 rounded-[2.5rem] bg-white border transition-all duration-500 hover:-translate-y-2 shadow-xl ${
                    CORES_BORDA[nivel] || 'border-text-muted/10'
                  }`}
                >
                  {nivel === 'ouro' && (
                    <div className="self-start px-4 py-1 rounded-full bg-amber-400 text-white text-[10px] font-black uppercase tracking-widest mb-6">
                      Mais Popular
                    </div>
                  )}
                  <div className="mb-8">
                    <h3 className="text-2xl font-black text-text-main mb-2 font-display capitalize">
                      {plano.nome}
                    </h3>
                    <p className="text-xs font-bold text-text-muted uppercase tracking-wide mb-6">
                      {DESTAQUE_DESCRICAO[nivel]}
                    </p>
                    <div className="flex items-baseline">
                      <span className="text-lg font-bold text-text-muted mr-1">R$</span>
                      <span className="text-5xl font-black text-text-main tracking-tighter">
                        {(plano.valor_centavos / 100).toFixed(2).replace('.', ',')}
                      </span>
                      <span className="text-text-muted font-bold text-sm ml-2">/mês</span>
                    </div>
                    <p className="text-xs text-text-muted/80 mt-2">
                      {ciclo === 'mensal' ? (
                        <>Total cobrado em 12x sem juros: {formatBRL(plano.valor_total_centavos)}</>
                      ) : (
                        <>Pagamento à vista de {formatBRL(plano.valor_total_centavos)} (10% off)</>
                      )}
                    </p>
                  </div>
                  <ul className="flex-grow space-y-3 mb-10">
                    {beneficios.map((b) => (
                      <li
                        key={b}
                        className="flex items-start text-sm font-medium text-text-main/80"
                      >
                        <CheckCircle2 className="h-5 w-5 text-primary mr-3 mt-0.5 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/signup?plano=${plano.sku}`}
                    className={`w-full py-4 rounded-2xl font-bold text-lg text-center transition-all ${
                      nivel === 'ouro'
                        ? 'bg-primary text-white hover:bg-primary-dark shadow-lg shadow-primary/20'
                        : 'bg-bg-light text-text-main hover:bg-white border border-text-muted/20'
                    }`}
                  >
                    Assinar Agora
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {/* Observações */}
        <div className="mt-16 p-12 rounded-[3rem] bg-text-main text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-[80px]"></div>
          <div className="relative z-10">
            <h3 className="text-2xl font-black mb-8 flex items-center font-display">
              <ShieldCheck className="h-8 w-8 text-primary mr-4" />
              Observações Importantes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <ul className="space-y-4 text-white/70 font-medium">
                <li className="flex items-center space-x-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>
                    Pagamentos mensais cobrados em 12 parcelas do valor apresentado.
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>Pagamento anual cobrado em uma única vez com 10% de desconto.</span>
                </li>
              </ul>
              <ul className="space-y-4 text-white/70 font-medium">
                <li className="flex items-center space-x-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>
                    Consulta Avulsa: R$ 59,90 (Clínico) / R$ 149,90 (Especialista, exclusivo
                    para assinantes).
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  <span>
                    (*) Programa Medicamento em Casa disponível para Brasília e Entorno.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
